import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { EmptyState } from '@/components/feedback/EmptyState'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Loading } from '@/components/feedback/Loading'
import { useToastStack } from '@/components/feedback/toast-stack'
import { DateInput } from '@/components/filters/DateInput'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useCashFirm, useInvalidateCash } from '@/features/cash/hooks'
import { CashFirmSelect } from '@/features/cash/shared'
import { HrPageHeader as PageHeader } from '@/features/hr/HrPageHeader'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { formatDateOnly, formatDecimal } from '@/lib/format'
import { messages } from '@/lib/messages'
import { toProblem } from '@/lib/problem'
import { queryKeys } from '@/lib/query-keys'
import { cn } from '@/lib/utils'
import { cashApi, type CashCari, type CashCheque } from '@/services/http/cash'

const t = messages.cash.cheques
const money = (v: number) => formatDecimal(v, 2)

const STATUS_TONE: Record<string, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  portfoyde: 'neutral',
  ciro_edilecek: 'info',
  tahsile_verilecek: 'success',
  tahsilde: 'success',
  haric: 'warning',
}

function useCashAction<T>(fn: (body: T) => Promise<unknown>) {
  const { toast } = useToastStack()
  const invalidate = useInvalidateCash()
  return {
    toast,
    mutation: useMutation({
      mutationFn: fn,
      onSuccess: () => void invalidate(),
      onError: (error) =>
        toast({
          type: 'error',
          title: messages.cash.planning.failed,
          description: toProblem(error).detail ?? undefined,
        }),
    }),
  }
}

/** Bir sonraki salı (ya da bugün salıysa bugün); ciro tarihi önerisi. */
function nextTuesday(from: string): string {
  const [y, m, d] = from.split('-').map(Number)
  const date = new Date(Date.UTC(y!, m! - 1, d!))
  date.setUTCDate(date.getUTCDate() + ((2 - date.getUTCDay() + 7) % 7))
  return date.toISOString().slice(0, 10)
}

function EndorseSheet({
  firm,
  cheque,
  start,
  onClose,
}: {
  firm: string
  cheque: CashCheque | null
  start: string
  onClose: () => void
}) {
  const [q, setQ] = useState('')
  const [cari, setCari] = useState<CashCari | null>(null)
  const [date, setDate] = useState(() => nextTuesday(start))
  const [note, setNote] = useState('')
  const debounced = useDebouncedValue(q.trim(), 300)
  const caris = useQuery({
    queryKey: queryKeys.cash.caris(firm, debounced),
    queryFn: () => cashApi.caris(firm, debounced),
    enabled: debounced.length >= 2,
  })
  const { toast, mutation } = useCashAction(cashApi.endorse)

  return (
    <Sheet open={cheque !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>{cheque ? t.endorseTitle(cheque.serial ?? cheque.key) : ''}</SheetTitle>
          <SheetDescription>
            {cheque ? `${formatDateOnly(cheque.due)} · ${money(cheque.amount)}` : ''}
          </SheetDescription>
        </SheetHeader>
        <SheetBody>
          <div className="flex flex-col gap-3">
            <label className="flex flex-col gap-1 text-xs">
              {t.cariSearch}
              <Input value={q} onChange={(e) => setQ(e.target.value)} aria-label={t.cariSearch} />
            </label>
            <p className="text-text-secondary text-xs">{t.cariHint}</p>
            {caris.isLoading && <Loading rows={3} />}
            <ul className="flex flex-col gap-1" aria-label={t.cariSearch}>
              {(caris.data ?? []).map((c) => (
                <li key={c.code}>
                  <button
                    type="button"
                    aria-label={t.selectCari(c.name ?? c.code)}
                    aria-pressed={cari?.code === c.code}
                    className={cn(
                      'hover:bg-surface-muted flex w-full items-center justify-between gap-2 rounded-md border px-2 py-1.5 text-left text-sm',
                      cari?.code === c.code && 'border-brand',
                    )}
                    onClick={() => setCari(c)}
                  >
                    <span className="min-w-0">
                      <span className="block truncate">{c.name ?? c.code}</span>
                      <span className="text-text-secondary text-xs">{c.code}</span>
                    </span>
                    <span className="text-text-secondary shrink-0 text-xs tabular-nums">
                      {c.has_open_items ? t.cariOpen(money(c.unplanned_total)) : t.cariNoOpen}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            {cari && <p className="text-sm font-medium">{t.selected(cari.name ?? cari.code)}</p>}
            <label className="flex flex-col gap-1 text-xs">
              {t.date}
              <DateInput value={date} onChange={setDate} aria-label={t.date} />
            </label>
            <label className="flex flex-col gap-1 text-xs">
              {t.note}
              <Input value={note} onChange={(e) => setNote(e.target.value)} aria-label={t.note} />
            </label>
          </div>
        </SheetBody>
        <SheetFooter>
          <Button
            disabled={!cari || !date || !cheque}
            loading={mutation.isPending}
            onClick={() =>
              mutation.mutate(
                {
                  firm,
                  cheque_key: cheque!.key,
                  cari_code: cari!.code,
                  date,
                  note: note || null,
                },
                {
                  onSuccess: (r) => {
                    const result = r as { distributed: number; advance: number }
                    toast({ type: 'success', title: t.endorsed(money(result.distributed)) })
                    if (result.advance > 0.005) {
                      toast({ type: 'warning', title: t.advance(money(result.advance)) })
                    }
                    onClose()
                  },
                },
              )
            }
          >
            {t.submit}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}

/** /nakit/cekler — çek portföyü ve "Çekle öde" (docs/NAKIT-PLANI-PLAN.md Faz 3, G1). */
export function ChequesPage() {
  const { firms, firm, setFirm, can } = useCashFirm()
  const canPlan = can('cash.plan')
  const [tab, setTab] = useState<'portfoy' | 'tahsil'>('portfoy')
  const [endorsing, setEndorsing] = useState<CashCheque | null>(null)
  const cheques = useQuery({
    queryKey: queryKeys.cash.cheques(firm ?? ''),
    queryFn: () => cashApi.cheques(firm!),
    enabled: !!firm,
  })
  const strip = useQuery({
    queryKey: queryKeys.cash.strip(firm ?? ''),
    queryFn: () => cashApi.strip(firm!),
    enabled: !!firm,
  })
  const unendorse = useCashAction((key: string) => cashApi.unendorse(firm!, key))
  const decide = useCashAction(cashApi.chequeDecision)

  const rows = (cheques.data ?? []).filter((c) => c.position === tab)
  const total = rows.reduce((sum, c) => sum + c.amount, 0)

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={t.title}
        description={t.description}
        actions={<CashFirmSelect firms={firms.data ?? []} firm={firm} onChange={setFirm} />}
      />
      <Tabs value={tab} onValueChange={(v) => setTab(v as 'portfoy' | 'tahsil')}>
        <TabsList>
          {(['portfoy', 'tahsil'] as const).map((key) => (
            <TabsTrigger key={key} value={key}>
              {t.tabs[key]}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      {cheques.isError ? (
        <ErrorState error={cheques.error} onRetry={() => void cheques.refetch()} />
      ) : !cheques.data || !firm || !strip.data ? (
        <Loading rows={8} />
      ) : rows.length === 0 ? (
        <EmptyState title={t.empty} description={t.description} />
      ) : (
        <div className="bg-surface overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-text-secondary border-b text-left text-xs">
                <th className="px-3 py-2 font-medium">{t.columns.due}</th>
                <th className="px-3 py-2 font-medium">{t.columns.serial}</th>
                <th className="px-3 py-2 font-medium">{t.columns.drawer}</th>
                <th className="px-3 py-2 text-right font-medium">{t.columns.amount}</th>
                <th className="px-3 py-2 font-medium">{t.columns.status}</th>
                <th className="px-3 py-2 font-medium">{t.columns.date}</th>
                <th className="px-3 py-2 font-medium">{t.columns.cari}</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => {
                const serial = c.serial ?? c.key
                const endorsed = c.status === 'ciro_edilecek'
                return (
                  <tr key={c.key} className="border-b last:border-0" data-key={c.key}>
                    <td className="px-3 py-2 whitespace-nowrap tabular-nums">
                      {formatDateOnly(c.due)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{serial}</td>
                    <td className="px-3 py-2">{c.drawer}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{money(c.amount)}</td>
                    <td className="px-3 py-2">
                      <Badge size="sm" tone={STATUS_TONE[c.status]} data-status={c.status}>
                        {t.status[c.status]}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap tabular-nums">
                      {formatDateOnly(c.ciro_date ?? c.collection_date)}
                    </td>
                    <td className="px-3 py-2">
                      {c.ciro_cari_code && (
                        <>
                          {c.ciro_cari_name ?? c.ciro_cari_code}
                          <span className="text-text-secondary block text-xs">
                            {c.ciro_cari_code}
                          </span>
                        </>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {canPlan && c.position === 'portfoy' && !c.excluded && !endorsed && (
                        <Button
                          size="sm"
                          aria-label={`${t.endorse} ${serial}`}
                          onClick={() => setEndorsing(c)}
                        >
                          {t.endorse}
                        </Button>
                      )}
                      {canPlan && endorsed && (
                        <Button
                          size="sm"
                          variant="secondary"
                          aria-label={`${t.unendorse} ${serial}`}
                          loading={unendorse.mutation.isPending}
                          onClick={() =>
                            unendorse.mutation.mutate(c.key, {
                              onSuccess: () =>
                                unendorse.toast({ type: 'success', title: t.unendorsed }),
                            })
                          }
                        >
                          {t.unendorse}
                        </Button>
                      )}
                      {canPlan && !endorsed && (
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label={`${c.excluded ? t.include : t.exclude} ${serial}`}
                          onClick={() =>
                            decide.mutation.mutate(
                              {
                                firm,
                                cheque_key: c.key,
                                excluded: !c.excluded,
                                planned_collection: c.planned_collection,
                              },
                              {
                                onSuccess: () => decide.toast({ type: 'success', title: t.saved }),
                              },
                            )
                          }
                        >
                          {c.excluded ? t.include : t.exclude}
                        </Button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
            <tfoot>
              <tr className="font-semibold">
                <td className="px-3 py-2" colSpan={3}>
                  {messages.cash.weekly.total}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{money(total)}</td>
                <td colSpan={4} />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      {firm && strip.data && (
        <EndorseSheet
          key={endorsing?.key ?? 'kapali'}
          firm={firm}
          cheque={endorsing}
          start={strip.data.start}
          onClose={() => setEndorsing(null)}
        />
      )}
    </div>
  )
}
